import { isDevPortalHost } from "./portal-host";

export const API_NAMESPACE_STORAGE_KEY = "debug-api-namespace";

export const API_NAMESPACE_MAX_LENGTH = 15;

/** A single lowercase DNS label: it becomes part of the API hostname. */
const API_NAMESPACE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** The one sessionStorage key a backend switch keeps: it is the visitor's, not the backend's. */
const LANGUAGE_STORAGE_KEY = "language";

export const isValidApiNamespace = (name: string): boolean =>
  name.length <= API_NAMESPACE_MAX_LENGTH && API_NAMESPACE_PATTERN.test(name);

/**
 * The API host of a namespaced dev deployment. folloapi's Deploy-k0s job, run
 * with namespaced=yes, deploys into k8s namespace dev-<user> and serves it on
 * api.dev-<user>.follosoft.com.
 */
export const namespacedApiHost = (name: string): string =>
  `api.dev-${name}.follosoft.com`;

/**
 * The build's API base pointed at a namespaced dev deployment. Only the origin
 * changes: the path (/v3) is the same on every deployment. A base that is not
 * an absolute http(s) URL is returned as it is.
 */
export const applyApiNamespace = (baseUrl: string, name: string): string =>
  baseUrl.replace(/^https?:\/\/[^/?#]+/i, `https://${namespacedApiHost(name)}`);

/**
 * The API base for this page: the build's, or — on a dev portal host with a
 * namespace set — the namespaced deployment's. Every other environment keeps
 * the build's base whatever is stored.
 */
export const resolveApiBaseUrl = (
  baseUrl: string,
  hostname: string,
  namespace: string | null,
): string => {
  if (
    !namespace ||
    !isValidApiNamespace(namespace) ||
    !isDevPortalHost(hostname)
  ) {
    return baseUrl;
  }

  return applyApiNamespace(baseUrl, namespace);
};

/** The namespace stored for this tab, or null when none (or nothing valid) is. */
export const readApiNamespace = (): string | null => {
  const name = sessionStorage.getItem(API_NAMESPACE_STORAGE_KEY);

  return name && isValidApiNamespace(name) ? name : null;
};

/**
 * Store a new namespace (null removes it). Everything else in sessionStorage —
 * the JWT, the adopted tenant uuid, the contract id, flow progress — was issued
 * by the backend the tab talked to until now and means nothing to another one,
 * so it goes too, as with the dev Logout button; only the language stays.
 *
 * Returns whether anything changed: saving the namespace already in use keeps
 * the session. The new base only applies from the next page load.
 */
export const switchApiNamespace = (name: string | null): boolean => {
  if (name !== null && !isValidApiNamespace(name)) {
    throw new Error(`"${name}" is not a valid API namespace`);
  }

  if (name === readApiNamespace()) {
    return false;
  }

  const language = sessionStorage.getItem(LANGUAGE_STORAGE_KEY);

  sessionStorage.clear();

  if (language !== null) {
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }

  if (name !== null) {
    sessionStorage.setItem(API_NAMESPACE_STORAGE_KEY, name);
  }

  return true;
};
